/** draft.ogreclan.com: the frame around every page, and which page an address shows */
import { render } from 'preact';
import { api, signInUrl } from './api';
import { useMe } from './me';
import { AccountPage } from './pages/account';
import { AdminPage } from './pages/admin';
import { ChatPage } from './pages/chat';
import { DiscordLeaguePage } from './pages/discord-league';
import { GuidesPage } from './pages/guides';
import { HomeSite } from './pages/home-site';
import { HomePage } from './pages/home';
import { LeaguePage } from './pages/league';
import { LinkingPage } from './pages/linking';
import { LeaguesPage } from './pages/leagues';
import { NewLeaguePage } from './pages/new-league';
import { PlanPage } from './pages/plan';
import { RosterScoresPage } from './pages/roster-scores';
import { SignInPage } from './pages/signin';
import { Link, usePath } from './router';
import { HOME_HOST, HOME_ORIGIN, LIVE } from './sites';
import { ThemePicker } from './theme';
import { Toasts } from './ui';

/** chat.ogreclan.com: OgreClan AI's own address, with the draft site a link away */
const CHAT_HOST = location.hostname.startsWith('chat.');
const DRAFT_SITE = CHAT_HOST ? `${location.protocol}//draft.${location.host.slice('chat.'.length)}` : '';

function ChatHeader() {
	const me = useMe();
	return <header class="top">
		<Link href="/" class="brand"><img src="/brand/logo-128.png" alt="OgreClan" />AI</Link>
		<nav class="nav">
			<Link href="/" class="active">Chat</Link>
			<a href={`${DRAFT_SITE}/league`}>Leagues</a>
			<a href={`${DRAFT_SITE}/power-rankings/`}>Power Rankings</a>
			<a href="https://play.ogreclan.com">Play</a>
		</nav>
		<span class="spacer" />
		<ThemePicker />
		{me?.user ? <a href={`${DRAFT_SITE}/account`} class="who">
			{me.user.avatar && <img src={me.user.avatar} alt="" />}
			<span>{me.user.showdown?.name || me.user.name}</span>
		</a> : me && <a class="button primary small" href={signInUrl()}>Sign in with Discord</a>}
	</header>;
}

function Header({ path }: { path: string }) {
	const me = useMe();
	const section = (prefix: string) => path === prefix || path.startsWith(`${prefix}/`) ? 'active' : '';
	return <header class="top">
		<Link href="/" class="brand"><img src="/brand/logo-128.png" alt="OgreClan" />Draft</Link>
		<nav class="nav">
			<Link href="/league" class={section('/league')}>Leagues</Link>
			<Link href="/plan" class={section('/plan')}>Planner</Link>
			<a href="/replays/">Replays</a>
			{/* every league's public Power Rankings (design/kitakami's pages, not this app) */}
			<a href="/power-rankings/">Power Rankings</a>
			{/* the guides live on ogreclan.com */}
			{LIVE ? <a href={`${HOME_ORIGIN}/guides`}>Guides</a> : <Link href="/guides" class={section('/guides')}>Guides</Link>}
			{me?.user && <Link href="/chat" class={section('/chat')}>Ask AI</Link>}
			<a href="https://play.ogreclan.com">Play</a>
			{me?.user?.isAdmin && <Link href="/admin" class={section('/admin')}>Admin</Link>}
		</nav>
		<span class="spacer" />
		<ThemePicker />
		{me?.user ? <Link href="/account" class="who">
			{me.user.avatar && <img src={me.user.avatar} alt="" />}
			<span>{me.user.showdown?.name || me.user.name}</span>
		</Link> : me && <a class="button primary small" href={signInUrl()}>Sign in with Discord</a>}
	</header>;
}

/** While an admin views the site as someone: who, that nothing can be changed, and the way out */
function ViewingAs() {
	const me = useMe();
	const user = me?.user;
	if (!user?.viewedBy) return null;
	return <div class="viewing-as" role="status">
		<span>Viewing the site as <strong>{user.showdown?.name || user.name}</strong>. It's read-only: nothing can be changed.</span>
		<button type="button" class="small" onClick={async () => {
			await api.delete('/api/admin/view-as');
			location.href = '/admin';
		}}>Stop viewing</button>
	</div>;
}

function NotFound() {
	return <div class="card"><h1>Page not found</h1><p>There's nothing at this address. <Link href="/league">See the leagues</Link>.</p></div>;
}

function App() {
	const path = usePath();
	const parts = path.split('/').filter(Boolean).map(decodeURIComponent);
	// ogreclan.com, the home site, has its own pages (pages/home-site.tsx)
	if (HOME_HOST) {
		return <>
			<ViewingAs />
			<HomeSite path={path} />
			<footer>
				OgreClan is a non-commercial fan project. Battles run on <a href="https://pokemonshowdown.com">Pokémon Showdown</a> (MIT
				licence). Pokémon is © Nintendo, Game Freak and The Pokémon Company.
			</footer>
			<Toasts />
		</>;
	}
	let page;
	// chat.ogreclan.com is OgreClan AI's own address (the same site)
	if (!parts.length && CHAT_HOST) page = <ChatPage />;
	else if (!parts.length) page = <HomePage />;
	else if (parts[0] === 'chat' && parts.length <= 2) page = <ChatPage id={parts[1]} />;
	else if (parts[0] === 'league' && parts.length === 1) page = <LeaguesPage />;
	else if (parts[0] === 'league' && parts[1] === 'new' && parts.length === 2) page = <NewLeaguePage />;
	else if (parts[0] === 'league' && parts.length <= 3) page = <LeaguePage roomid={parts[1]!} tab={parts[2]} />;
	else if (parts[0] === 'discord' && (parts.length === 2 || parts.length === 3)) page = <DiscordLeaguePage id={parts[1]!} tab={parts[2]} />;
	else if (parts[0] === 'admin' && parts.length === 1) page = <AdminPage />;
	else if (parts[0] === 'admin' && parts[1] === 'linking' && parts.length === 2) page = <LinkingPage />;
	else if (parts[0] === 'admin' && parts[1] === 'scores' && parts.length === 2) page = <RosterScoresPage />;
	else if (parts[0] === 'plan' && parts.length === 1) page = <PlanPage />;
	else if (parts[0] === 'account' && parts.length === 1) page = <AccountPage />;
	else if (parts[0] === 'signin' && parts.length === 1) page = <SignInPage />;
	else if (parts[0] === 'guides' && parts.length <= 2 && LIVE && !CHAT_HOST) {
		// the guides moved to ogreclan.com
		location.replace(`${HOME_ORIGIN}${path}${location.hash}`);
		page = null;
	}
	else if (parts[0] === 'guides' && parts.length <= 2) page = <GuidesPage section={parts[1] || 'start'} />;
	// Power Rankings and the stream kit are their own pages (design/kitakami, copied there by build.mjs), not this app
	else if (parts[0] === 'power-rankings' || parts[0] === 'stream') {
		location.replace(`/power-rankings/${parts[0] === 'stream' ? 'stream' : 'index'}.html${location.search}${location.hash}`);
		page = null;
	}
	else page = <NotFound />;
	return <>
		<ViewingAs />
		{CHAT_HOST ? <ChatHeader /> : <Header path={path} />}
		<main>{page}</main>
		<footer>
			OgreClan is a non-commercial fan project. Battles run on <a href="https://pokemonshowdown.com">Pokémon Showdown</a> (MIT
			licence). Pokémon is © Nintendo, Game Freak and The Pokémon Company. Mask icons by loran_hemlock.
		</footer>
		<Toasts />
	</>;
}

const root = document.getElementById('app')!;
if (HOME_HOST) document.title = 'OgreClan';
// the "Loading…" shown until the app starts (Preact would leave it in place)
root.textContent = '';
render(<App />, root);
